import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

interface OnboardingStep {
  titleKey: string;
  /** One paragraph per key, so a step can carry a second, separate statement. */
  bodyKeys: string[];
  /** Highlighted below the text. */
  noteKey?: string;
  /** The answer the user gives to move on. */
  replyKey: string;
}

/** The information steps; the terms of use follow as the last step. */
const STEPS: OnboardingStep[] = [
  { titleKey: 'colimind.onboardingWelcomeTitle', bodyKeys: ['colimind.onboardingWelcomeBody'], replyKey: 'colimind.onboardingReplyStart' },
  { titleKey: 'colimind.onboardingWhatTitle', bodyKeys: ['colimind.onboardingWhatBody'], replyKey: 'colimind.onboardingReplyGood' },
  {
    titleKey: 'colimind.onboardingAiTitle',
    bodyKeys: ['colimind.onboardingAiBody'],
    noteKey: 'colimind.onboardingAiCaveat',
    replyKey: 'colimind.onboardingReplyUnderstood',
  },
  { titleKey: 'colimind.onboardingPrivacyTitle', bodyKeys: ['colimind.onboardingPrivacyBody'], replyKey: 'colimind.onboardingReplyOk' },
  { titleKey: 'colimind.onboardingDataTitle', bodyKeys: ['colimind.onboardingDataBody'], replyKey: 'colimind.onboardingReplyGotIt' },
];

const TERMS_TITLE = 'colimind.onboardingTermsTitle';

type Entry =
  | { kind: 'step'; step: number }
  | { kind: 'terms'; again: boolean }
  | { kind: 'declined' }
  | { kind: 'done' }
  | { kind: 'user'; textKey: string };

export const TYPING_MS = 700;

/**
 * Onboarding shown over the CoLiMind page on first use, as a short conversation. The chat stays
 * hidden until the user has accepted the terms of use and started chatting.
 */
@Component({
  selector: 'colimo-onboarding',
  standalone: true,
  imports: [MatIconModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './colimind-onboarding.component.html',
  styleUrls: ['./colimind-onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
  @Input() userName = '';
  @Output() termsAccepted = new EventEmitter<void>();
  /** Emitted once the user starts chatting after accepting the terms. */
  @Output() completed = new EventEmitter<void>();

  readonly steps = STEPS;
  readonly totalSteps = STEPS.length + 1;
  readonly segments = Array.from({ length: this.totalSteps }, (_, i) => i);
  readonly entries = signal<Entry[]>([]);
  readonly typing = signal(false);
  readonly termsChecked = signal(false);
  readonly termsOpen = signal(false);

  /** The message the user answers next, undefined while CoLiMind is typing. */
  readonly last = computed(() => {
    const entry = this.entries().at(-1);
    return !this.typing() && entry?.kind !== 'user' ? entry : undefined;
  });

  readonly progress = computed(() => {
    const bot = this.entries().filter(e => e.kind !== 'user').at(-1);
    if (!bot || bot.kind === 'step') {
      const step = bot?.kind === 'step' ? bot.step : 0;
      return { step: step + 1, done: step, titleKey: STEPS[step].titleKey };
    }
    const done = bot.kind === 'done' ? this.totalSteps : STEPS.length;
    return { step: this.totalSteps, done, titleKey: TERMS_TITLE };
  });

  @ViewChild('thread') private thread?: ElementRef<HTMLElement>;

  /** What the user answers with takes the focus, so the conversation also works with the keyboard. */
  @ViewChild('focusTarget') set focusTarget(target: ElementRef<HTMLElement> | undefined) {
    if (!target) return;
    target.nativeElement.focus({ preventScroll: true });
    this.scrollToEnd();
  }

  private timer?: ReturnType<typeof setTimeout>;
  private scrollTimer?: ReturnType<typeof setTimeout>;
  private readonly delay = matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : TYPING_MS;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.timer);
      clearTimeout(this.scrollTimer);
    });
  }

  ngOnInit(): void {
    this.write({ kind: 'step', step: 0 });
  }

  next(step: number): void {
    const last = STEPS.length - 1;
    this.reply(STEPS[step].replyKey, step < last ? { kind: 'step', step: step + 1 } : { kind: 'terms', again: false });
  }

  acceptTerms(): void {
    if (!this.termsChecked() || this.last()?.kind !== 'terms') return;
    this.termsAccepted.emit();
    this.reply('colimind.onboardingTermsAcceptedReply', { kind: 'done' });
  }

  declineTerms(): void {
    this.reply('colimind.onboardingTermsDeclinedReply', { kind: 'declined' });
  }

  reviewTerms(): void {
    this.reply('colimind.onboardingTermsReview', { kind: 'terms', again: true });
  }

  start(): void {
    if (this.last()?.kind === 'done') this.completed.emit();
  }

  isLast(entry: Entry): boolean {
    return entry === this.last();
  }

  /** Answers the last message and lets CoLiMind write the next one. */
  private reply(textKey: string, next: Entry): void {
    if (!this.last()) return;
    this.entries.update(e => [...e, { kind: 'user', textKey }]);
    this.write(next);
  }

  private write(entry: Entry): void {
    this.typing.set(true);
    this.scrollToEnd();
    this.timer = setTimeout(() => {
      if (entry.kind === 'terms') {
        this.termsChecked.set(false);
        this.termsOpen.set(false);
      }
      this.typing.set(false);
      this.entries.update(e => [...e, entry]);
      this.scrollToEnd();
    }, this.delay);
  }

  private scrollToEnd(): void {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      const el = this.thread?.nativeElement;
      el?.scrollTo({ top: el.scrollHeight, behavior: this.delay ? 'smooth' : 'auto' });
    });
  }
}
